import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AcademicYearService } from '../../../services';

@Component({
  selector: 'app-academic-years',
  standalone: false,
  templateUrl: './academic-years.html',
  styleUrl: './academic-years.css',
})
export class AcademicYears implements OnInit {

  loading = false;
  errorMsg = '';
  allYears: any[] = [];
  filteredYears: any[] = [];
  pagedYears: any[] = [];

  searchQuery = '';
  page = 0;
  size = 10;

  showAddModal = false;
  showDeleteModal = false;
  showSetCurrentModal = false;

  selectedYear: any = null;

  addForm = { name: '', startDate: '', endDate: '', setAsCurrent: false };

  addLoading = false;
  deleteLoading = false;
  setCurrentLoading = false;
  setCurrentLoadingId: number | null = null;

  addError = '';
  deleteError = '';
  setCurrentError = '';

  constructor(
    private academicYearService: AcademicYearService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchYears();
  }

  fetchYears() {
    this.loading = true;
    this.errorMsg = '';
    this.cdr.detectChanges();
    this.academicYearService.getAcademicYears().subscribe({
      next: (res: any) => {
        this.allYears = res?.data ?? [];
        this.applyFilter();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.errorMsg = err?.error?.message || 'Failed to load academic years.';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  applyFilter() {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredYears = q
      ? this.allYears.filter(y => y.name?.toLowerCase().includes(q))
      : [...this.allYears];
    this.page = 0;
    this.updatePage();
  }

  updatePage() {
    const start = this.page * this.size;
    this.pagedYears = this.filteredYears.slice(start, start + this.size);
  }

  get totalPages(): number { return Math.ceil(this.filteredYears.length / this.size); }
  get hasNext(): boolean { return this.page < this.totalPages - 1; }
  get hasPrev(): boolean { return this.page > 0; }

  nextPage() { if (!this.hasNext) return; this.page++; this.updatePage(); }
  prevPage() { if (!this.hasPrev) return; this.page--; this.updatePage(); }
  onSearch() { this.applyFilter(); }
  getMinValue(a: number, b: number) { return Math.min(a, b); }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getDurationMonths(start: string, end: string): string {
    if (!start || !end) return '';
    const s = new Date(start);
    const e = new Date(end);
    const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    return months > 0 ? `${months} months` : '';
  }

  openAddModal() {
    this.addForm = { name: '', startDate: '', endDate: '', setAsCurrent: false };
    this.addError = '';
    this.showAddModal = true;
  }

  closeAddModal() {
    if (this.addLoading) return;
    this.showAddModal = false;
    this.addError = '';
  }

  submitAdd() {
    if (!this.addForm.name.trim() || !this.addForm.startDate || !this.addForm.endDate) {
      this.addError = 'Name, start date, and end date are required.';
      return;
    }
    if (new Date(this.addForm.endDate) <= new Date(this.addForm.startDate)) {
      this.addError = 'End date must be after start date.';
      return;
    }
    this.addLoading = true;
    this.addError = '';
    this.academicYearService.createAcademicYear(this.addForm).subscribe({
      next: () => {
        this.addLoading = false;
        this.showAddModal = false;
        this.fetchYears();
      },
      error: (err: any) => {
        this.addLoading = false;
        this.addError = err?.error?.message || 'Failed to create academic year.';
        this.cdr.detectChanges();
      }
    });
  }

  openSetCurrentModal(year: any) {
    this.selectedYear = year;
    this.setCurrentError = '';
    this.showSetCurrentModal = true;
  }

  closeSetCurrentModal() {
    if (this.setCurrentLoading) return;
    this.showSetCurrentModal = false;
    this.setCurrentError = '';
    this.selectedYear = null;
  }

  confirmSetCurrent() {
    if (!this.selectedYear) return;
    this.setCurrentLoading = true;
    this.setCurrentError = '';
    this.academicYearService.setCurrentAcademicYear(this.selectedYear.id).subscribe({
      next: () => {
        this.setCurrentLoading = false;
        this.showSetCurrentModal = false;
        this.fetchYears();
      },
      error: (err: any) => {
        this.setCurrentLoading = false;
        this.setCurrentError = err?.error?.message || 'Failed to set current year.';
        this.cdr.detectChanges();
      }
    });
  }

  openDeleteModal(year: any) {
    this.selectedYear = year;
    this.deleteError = '';
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    if (this.deleteLoading) return;
    this.showDeleteModal = false;
    this.deleteError = '';
    this.selectedYear = null;
  }

  confirmDelete() {
    this.deleteLoading = false;
    this.showDeleteModal = false;
    this.selectedYear = null;
  }
}